import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

interface MyAd {
  id: number;
  title: string;
  price: number | string;
  price_type: string;
  status: string;
  views_count: number;
  favorites_count: number;
  is_featured: boolean;
  published_at: string | null;
  created_at: string;
  expires_at: string | null;
  category: { id: number; name_ar: string; slug?: string } | null;
  city: { id?: number; name_ar: string } | null;
  images: { image_url: string; thumbnail_url: string | null; is_primary: boolean }[];
}

interface Pagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

type StatusFilter = '' | 'active' | 'pending' | 'rejected' | 'expired' | 'sold';

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: '', label: 'الكل' },
  { value: 'active', label: 'منشور' },
  { value: 'pending', label: 'قيد المراجعة' },
  { value: 'rejected', label: 'مرفوض' },
  { value: 'expired', label: 'منتهي' },
  { value: 'sold', label: 'مباع' },
];

const STATUS_BADGE: Record<string, { label: string; bg: string; color: string }> = {
  active:    { label: 'منشور',       bg: '#E6F4EA', color: '#1E7A3C' },
  pending:   { label: 'قيد المراجعة', bg: '#FEF3C7', color: '#92400E' },
  rejected:  { label: 'مرفوض',       bg: '#FEE2E2', color: '#B91C1C' },
  expired:   { label: 'منتهي',       bg: '#F3F4F6', color: '#4B5563' },
  restricted:{ label: 'مقيّد',        bg: '#FEE2E2', color: '#B91C1C' },
  sold:      { label: 'مباع',        bg: '#E0F2FE', color: '#075985' },
  draft:     { label: 'مسودة',       bg: '#F5F5F5', color: '#616161' },
  suspended: { label: 'معلّق',       bg: '#FDE68A', color: '#78350F' },
};

const timeAgo = (d: string | null): string => {
  if (!d) return '—';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'الآن';
  if (s < 3600) return 'منذ ' + Math.floor(s / 60) + ' د';
  if (s < 86400) return 'منذ ' + Math.floor(s / 3600) + ' س';
  if (s < 2592000) return 'منذ ' + Math.floor(s / 86400) + ' يوم';
  if (s < 31536000) return 'منذ ' + Math.floor(s / 2592000) + ' شهر';
  return 'منذ ' + Math.floor(s / 31536000) + ' سنة';
};

const MyAdsPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [status, setStatus] = useState<StatusFilter>('');
  const [page, setPage] = useState(1);
  const [statsAdId, setStatsAdId] = useState<number | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['my-ads', status, page],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, per_page: 12 };
      if (status) params.status = status;
      const res = await api.get('/my/ads', { params });
      return res.data.data as { ads: MyAd[]; pagination: Pagination; counts?: Record<string, number> };
    },
    enabled: isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const ads = data?.ads || [];
  const pagination = data?.pagination;

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete('/ads/' + id),
    onSuccess: () => {
      toast.success('تم حذف الإعلان');
      qc.invalidateQueries({ queryKey: ['my-ads'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'تعذر الحذف');
    },
  });

  const soldMut = useMutation({
    mutationFn: async (id: number) => api.post('/ads/' + id + '/mark-as-sold'),
    onSuccess: () => {
      toast.success('تم تعليم الإعلان كمباع');
      qc.invalidateQueries({ queryKey: ['my-ads'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'تعذر تنفيذ العملية');
    },
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['ad-stats', statsAdId],
    queryFn: async () => {
      const res = await api.get('/ads/' + statsAdId + '/stats');
      return res.data.data as any;
    },
    enabled: statsAdId != null,
  });

  const renewMut = useMutation({
    mutationFn: async (id: number) => api.post('/ads/' + id + '/renew'),
    onSuccess: () => {
      toast.success('تم تجديد الإعلان');
      qc.invalidateQueries({ queryKey: ['my-ads'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'تعذر التجديد');
    },
  });

  const onDelete = (ad: MyAd) => {
    if (!window.confirm('حذف الإعلان "' + ad.title + '"؟ لا يمكن التراجع.')) return;
    deleteMut.mutate(ad.id);
  };

  const onRenew = (ad: MyAd) => {
    if (!window.confirm('تجديد الإعلان "' + ad.title + '"؟')) return;
    renewMut.mutate(ad.id);
  };

  const onMarkSold = (ad: MyAd) => {
    if (!window.confirm('تعليم الإعلان "' + ad.title + '" كمباع؟')) return;
    soldMut.mutate(ad.id);
  };

  if (authLoading || (!isAuthenticated && !authLoading)) {
    return (
      <div className="mad-loading" style={{ fontFamily: "'Cairo',sans-serif", textAlign: 'center', padding: '60px 20px', color: '#9E9E9E' }}>
        جاري التحميل...
      </div>
    );
  }

  return (
    <>
      <style>{`
        .mad{max-width:1100px;margin:0 auto;padding:20px 16px;font-family:'Cairo',sans-serif}
        .mad *{box-sizing:border-box}

        .mad-head{display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:16px}
        .mad-title{font-size:24px;font-weight:800;color:#212121;margin:0}
        .mad-sub{font-size:13px;color:#757575;margin:4px 0 0}
        .mad-new{display:inline-flex;align-items:center;gap:8px;background:#005343;color:#fff;padding:11px 22px;border-radius:10px;text-decoration:none;font-weight:700;font-size:14px;border:none;cursor:pointer;font-family:'Cairo',sans-serif;transition:all 0.15s;box-shadow:0 2px 6px rgba(17,94,84,0.25)}
        .mad-new:hover{background:#0d4a42;transform:translateY(-1px);box-shadow:0 4px 10px rgba(17,94,84,0.35)}

        .mad-tabs{display:flex;gap:6px;border-bottom:2px solid #E0E0E0;overflow-x:auto;margin-bottom:18px;padding-bottom:0}
        .mad-tab{padding:11px 20px;font-size:14px;font-weight:700;color:#757575;background:none;border:none;border-bottom:3px solid transparent;cursor:pointer;font-family:'Cairo',sans-serif;white-space:nowrap;margin-bottom:-2px;transition:all 0.15s}
        .mad-tab:hover{color:#424242}
        .mad-tab.active{color:#005343;border-bottom-color:#005343}
        .mad-tab-count{display:inline-block;margin-right:6px;background:#E0E0E0;color:#616161;font-size:11px;padding:1px 7px;border-radius:9999px;font-weight:700}
        .mad-tab.active .mad-tab-count{background:#005343;color:#fff}

        .mad-grid{display:flex;flex-direction:column;gap:12px}
        .mad-card{background:#fff;border:1px solid #E5E7EB;border-radius:14px;padding:10px;display:flex;gap:11px;align-items:stretch;transition:all 0.15s;position:relative;box-shadow:0 1px 3px rgba(0,0,0,.05)}
        .mad-card:hover{box-shadow:0 4px 14px rgba(0,0,0,0.07);border-color:#BFDEDA}
        .mad-img{width:92px;min-height:92px;align-self:stretch;flex-shrink:0;background:#F5F5F5;position:relative;overflow:hidden;border-radius:11px}
        .mad-img img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
        .mad-img-empty{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#E6F2F0,#BFDEDA);color:#005343;font-size:26px}
        .mad-feat{position:absolute;top:5px;right:5px;background:rgba(217,119,6,0.95);color:#fff;font-size:9.5px;font-weight:800;padding:2px 7px;border-radius:9999px;backdrop-filter:blur(8px);z-index:1}

        .mad-body{flex:1;padding:2px 0;display:flex;flex-direction:column;justify-content:space-between;min-width:0}
        .mad-top{display:flex;justify-content:space-between;align-items:flex-start;gap:10px;margin-bottom:6px}
        .mad-card-title{font-size:15px;font-weight:800;color:#005343;margin:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1;text-decoration:none}
        .mad-card-title:hover{color:#0d4a42}
        .mad-price{font-size:15px;font-weight:800;color:#005343;white-space:nowrap;flex-shrink:0;font-family:'Inter','Cairo',sans-serif}
        .mad-price small{font-size:10.5px;font-weight:600;color:#757575;font-family:'Cairo',sans-serif}

        .mad-meta{display:flex;gap:8px;flex-wrap:wrap;font-size:11px;color:#757575;margin-bottom:8px;align-items:center}
        .mad-meta-item{display:flex;align-items:center;gap:3px}
        .mad-status{padding:2px 9px;border-radius:9999px;font-size:10.5px;font-weight:700}

        .mad-actions{display:flex;gap:6px;flex-wrap:wrap;margin-top:auto;padding-top:8px;border-top:1px solid #F5F5F5}
        .mad-btn{display:inline-flex;align-items:center;gap:4px;padding:6px 11px;border-radius:8px;font-size:11.5px;font-weight:700;cursor:pointer;border:1.5px solid #E0E0E0;background:#fff;color:#424242;text-decoration:none;font-family:'Cairo',sans-serif;transition:all 0.15s}
        .mad-btn:hover{border-color:#005343;color:#005343;background:#E6F2F0}
        .mad-btn.primary{background:#005343;border-color:#005343;color:#fff}
        .mad-btn.primary:hover{background:#0d4a42;color:#fff}
        .mad-btn.amber{background:#B8893C;border-color:#B8893C;color:#fff}
        .mad-btn.amber:hover{background:#B45309;border-color:#B45309;color:#fff}
        .mad-btn.danger{color:#B91C1C;border-color:#FECACA}
        .mad-btn.danger:hover{background:#FEE2E2;border-color:#F87171;color:#B91C1C}
        .mad-btn:disabled{opacity:0.5;cursor:not-allowed}
        .mad-stats-overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:3000;padding:16px}
        .mad-stats-modal{background:#fff;border-radius:16px;width:100%;max-width:420px;padding:20px;box-shadow:0 20px 50px rgba(0,0,0,.25)}
        .mad-stats-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px}
        .mad-stats-head h3{margin:0;font-size:17px;font-weight:800;color:#212121}
        .mad-stats-close{width:32px;height:32px;border:none;background:#F3F4F6;border-radius:8px;cursor:pointer;font-size:14px;color:#616161}
        .mad-stats-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
        .mad-stat-box{background:#F7F9F8;border:1px solid #E6F2F0;border-radius:12px;padding:14px;text-align:center;display:flex;flex-direction:column;gap:4px}
        .mad-stat-box .v{font-size:20px;font-weight:800;color:#005343}
        .mad-stat-box .l{font-size:11.5px;color:#757575;font-weight:600}

        .mad-empty{background:#fff;border:1px solid #E0E0E0;border-radius:12px;padding:60px 20px;text-align:center}
        .mad-empty-icon{font-size:52px;display:block;margin-bottom:14px;opacity:0.6}
        .mad-empty-t{font-size:17px;font-weight:700;color:#424242;margin:0 0 8px}
        .mad-empty-s{font-size:13px;color:#9E9E9E;margin:0 0 20px}

        .mad-pagination{display:flex;justify-content:center;align-items:center;gap:6px;margin:24px 0 20px}
        .mad-pg{min-width:38px;height:38px;border:1.5px solid #E0E0E0;background:#fff;border-radius:8px;cursor:pointer;font-family:'Inter','Cairo',sans-serif;font-size:13px;font-weight:600;color:#616161;padding:0 10px;transition:all 0.15s}
        .mad-pg:hover:not(:disabled){border-color:#005343;color:#005343}
        .mad-pg:disabled{opacity:0.4;cursor:default}
        .mad-pg.active{background:#005343;border-color:#005343;color:#fff}

        .mad-loading{text-align:center;padding:50px 20px;color:#9E9E9E;font-size:14px}
        .mad-fetching{height:2px;background:linear-gradient(90deg,transparent,#005343,transparent);background-size:200% 100%;animation:madLoad 1.2s linear infinite;border-radius:2px;margin-bottom:10px}
        @keyframes madLoad{0%{background-position:200% 0}100%{background-position:-200% 0}}

        @media (max-width:640px){
          .mad-tab{padding:10px 14px;font-size:13px}
          .mad-actions .mad-btn{flex:1;justify-content:center}
        }
      `}</style>

      <div className="mad">
        <div className="mad-head">
          <div>
            <h1 className="mad-title">إعلاناتي</h1>
            {pagination && <p className="mad-sub">{pagination.total} إعلان</p>}
          </div>
          <Link to="/ads/create" className="mad-new">
            <span style={{ fontSize: 18, lineHeight: 1 }}>＋</span>
            إنشاء إعلان جديد
          </Link>
        </div>

        <div className="mad-tabs">
          {STATUS_TABS.map(t => {
            const key = t.value || 'all';
            const c = data?.counts?.[key];
            return (
              <button
                key={key}
                className={'mad-tab' + (status === t.value ? ' active' : '')}
                onClick={() => { setStatus(t.value); setPage(1); }}
              >
                {t.label}
                {typeof c === 'number' && c > 0 && <span className="mad-tab-count">{c}</span>}
              </button>
            );
          })}
        </div>

        {isFetching && !isLoading && <div className="mad-fetching" />}

        {isLoading ? (
          <div className="mad-loading">جاري التحميل...</div>
        ) : isError ? (
          <div className="mad-empty">
            <span className="mad-empty-icon">⚠️</span>
            <p className="mad-empty-t">تعذر تحميل الإعلانات</p>
            <p className="mad-empty-s">حاول مرة أخرى لاحقاً</p>
          </div>
        ) : ads.length === 0 ? (
          <div className="mad-empty">
            <span className="mad-empty-icon">📭</span>
            <p className="mad-empty-t">
              {status ? 'لا توجد إعلانات في هذه الحالة' : 'لم تنشر أي إعلان بعد'}
            </p>
            <p className="mad-empty-s">
              {status ? 'جرّب تبويب حالة أخرى' : 'أنشئ أول إعلان لك الآن وابدأ بالبيع'}
            </p>
            {!status && (
              <Link to="/ads/create" className="mad-new">
                <span style={{ fontSize: 18, lineHeight: 1 }}>＋</span>
                إنشاء إعلان جديد
              </Link>
            )}
          </div>
        ) : (
          <div className="mad-grid">
            {ads.map(ad => {
              const img = ad.images?.find(i => i.is_primary) || ad.images?.[0];
              const badge = STATUS_BADGE[ad.status] || { label: ad.status, bg: '#F5F5F5', color: '#616161' };
              const canRenew = ad.status === 'expired' || ad.status === 'active';
              // نافذة التعديل: 10 دقائق من النشر، أو دائماً للإعلان "المقيّد" (لتصحيحه)
              const editBase = ad.published_at || ad.created_at;
              const editable = ad.status === 'restricted'
                || (!!editBase && (Date.now() - new Date(editBase).getTime()) <= 10 * 60 * 1000);
              const price = typeof ad.price === 'string' ? parseFloat(ad.price) : ad.price;
              return (
                <div key={ad.id} className="mad-card">
                  <Link to={'/ads/' + ad.id} className="mad-img" style={{ textDecoration: 'none' }}>
                    {img ? (
                      <img src={img.thumbnail_url || img.image_url} alt={ad.title} />
                    ) : (
                      <div className="mad-img-empty">📷</div>
                    )}
                    {ad.is_featured && <span className="mad-feat">⭐ مميز</span>}
                  </Link>
                  <div className="mad-body">
                    <div>
                      <div className="mad-top">
                        <Link to={'/ads/' + ad.id} className="mad-card-title">{ad.title}</Link>
                        <div className="mad-price">
                          {Number.isFinite(price) ? Number(price).toLocaleString() : ad.price} <small>ر.س</small>
                        </div>
                      </div>
                      <div className="mad-meta">
                        <span className="mad-status" style={{ background: badge.bg, color: badge.color }}>
                          {badge.label}
                        </span>
                        {ad.category && <span className="mad-meta-item">📂 {ad.category.name_ar}</span>}
                        {ad.city && <span className="mad-meta-item">📍 {ad.city.name_ar}</span>}
                        <span className="mad-meta-item">👁 {ad.views_count || 0}</span>
                        <span className="mad-meta-item">♡ {ad.favorites_count || 0}</span>
                        <span className="mad-meta-item">🕐 {timeAgo(ad.published_at || ad.created_at)}</span>
                      </div>
                    </div>
                    <div className="mad-actions">
                      <Link to={'/ads/' + ad.id} className="mad-btn">👁 عرض</Link>
                      {editable && <Link to={'/ads/' + ad.id + '/edit'} className="mad-btn primary">✎ تعديل</Link>}
                      {canRenew && (
                        <button
                          className="mad-btn"
                          onClick={() => onRenew(ad)}
                          disabled={renewMut.isPending}
                        >
                          🔄 تجديد
                        </button>
                      )}
                      <button className="mad-btn" onClick={() => setStatsAdId(ad.id)}>📊 إحصائيات</button>
                      {ad.status === 'active' && (
                        <button className="mad-btn" onClick={() => onMarkSold(ad)} disabled={soldMut.isPending}>✓ تم البيع</button>
                      )}
                      {ad.status === 'active' && (
                        <Link to={'/my/ads/' + ad.id + '/pin'} className="mad-btn amber">
                          📌 تثبيت
                        </Link>
                      )}
                      <button
                        className="mad-btn danger"
                        onClick={() => onDelete(ad)}
                        disabled={deleteMut.isPending}
                      >
                        🗑 حذف
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {pagination && pagination.last_page > 1 && (
          <div className="mad-pagination">
            <button
              className="mad-pg"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              →
            </button>
            {Array.from({ length: Math.min(pagination.last_page, 5) }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                className={'mad-pg' + (page === p ? ' active' : '')}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            ))}
            {pagination.last_page > 5 && <span style={{ color: '#9E9E9E', fontSize: 13 }}>...</span>}
            {pagination.last_page > 5 && (
              <button
                className={'mad-pg' + (page === pagination.last_page ? ' active' : '')}
                onClick={() => setPage(pagination.last_page)}
              >
                {pagination.last_page}
              </button>
            )}
            <button
              className="mad-pg"
              onClick={() => setPage(p => Math.min(pagination.last_page, p + 1))}
              disabled={page === pagination.last_page}
            >
              ←
            </button>
          </div>
        )}
      </div>

      {statsAdId != null && (
        <div className="mad-stats-overlay" onClick={() => setStatsAdId(null)}>
          <div className="mad-stats-modal" onClick={e => e.stopPropagation()}>
            <div className="mad-stats-head">
              <h3>📊 إحصائيات الإعلان</h3>
              <button className="mad-stats-close" onClick={() => setStatsAdId(null)}>✕</button>
            </div>
            {statsLoading ? (
              <p style={{ textAlign: 'center', color: '#9E9E9E', padding: '24px 0' }}>جاري التحميل...</p>
            ) : statsData ? (
              <div className="mad-stats-grid">
                <div className="mad-stat-box"><span className="v">{statsData.views_total ?? 0}</span><span className="l">إجمالي المشاهدات</span></div>
                <div className="mad-stat-box"><span className="v">{statsData.views_today ?? 0}</span><span className="l">مشاهدات اليوم</span></div>
                <div className="mad-stat-box"><span className="v">{statsData.views_week ?? 0}</span><span className="l">مشاهدات الأسبوع</span></div>
                <div className="mad-stat-box"><span className="v">{statsData.favorites_count ?? 0}</span><span className="l">المفضّلة</span></div>
                <div className="mad-stat-box"><span className="v">{statsData.renewal_count ?? 0}</span><span className="l">مرات التجديد</span></div>
                <div className="mad-stat-box"><span className="v">{statsData.expires_at ? new Date(statsData.expires_at).toLocaleDateString('ar-SA') : '—'}</span><span className="l">ينتهي في</span></div>
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#9E9E9E', padding: '24px 0' }}>تعذّر تحميل الإحصائيات</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MyAdsPage;
